// Web Crypto API utilities for zero-knowledge encryption
// All operations happen in-browser, no secrets leave the client

const ALGORITHM = 'AES-GCM'
const KEY_ALGO = 'PBKDF2'
const HASH = 'SHA-256'
const ITERATIONS = 200000
const KEY_LENGTH = 256
const IV_LENGTH = 12
const SALT_LENGTH = 32

function bufToHex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

function hexToBuf(hex) {
  return new Uint8Array(hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16))).buffer
}

export async function deriveKey(password, salt) {
  const encoder = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: KEY_ALGO },
    false,
    ['deriveBits', 'deriveKey']
  )
  return crypto.subtle.deriveKey(
    {
      name: KEY_ALGO,
      salt,
      iterations: ITERATIONS,
      hash: HASH
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  )
}

export async function generateSalt() {
  return crypto.getRandomValues(new Uint8Array(SALT_LENGTH))
}

export async function generateIV() {
  return crypto.getRandomValues(new Uint8Array(IV_LENGTH))
}

export async function encrypt(plaintext, key, iv) {
  const encoder = new TextEncoder()
  const data = encoder.encode(JSON.stringify(plaintext))
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    data
  )
  return ciphertext
}

export async function decrypt(ciphertext, key, iv) {
  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext
  )
  const decoder = new TextDecoder()
  return JSON.parse(decoder.decode(decrypted))
}

export async function wrapData(data, password) {
  const salt = await generateSalt()
  const iv = await generateIV()
  const key = await deriveKey(password, salt)
  const ciphertext = await encrypt(data, key, iv)
  return {
    ciphertext: bufToHex(ciphertext),
    iv: bufToHex(iv),
    salt: bufToHex(salt)
  }
}

export async function unwrapData({ ciphertext, iv, salt }, password) {
  const key = await deriveKey(password, hexToBuf(salt))
  const plaintext = await decrypt(hexToBuf(ciphertext), key, hexToBuf(iv))
  return plaintext
}

// RFC 6238 TOTP using Web Crypto (HMAC-SHA1). Browser-safe, no node deps.
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

function base32Decode(input) {
  const clean = input.replace(/=+$/, '').toUpperCase().replace(/\s/g, '')
  let bits = ''
  for (const c of clean) {
    const idx = BASE32_ALPHABET.indexOf(c)
    if (idx === -1) continue
    bits += idx.toString(2).padStart(5, '0')
  }
  const bytes = []
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    bytes.push(parseInt(bits.slice(i, i + 8), 2))
  }
  return new Uint8Array(bytes)
}

async function hmacSha1(key, msg) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw', key,
    { name: 'HMAC', hash: 'SHA-1' },
    false, ['sign']
  )
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, msg)
  return new Uint8Array(sig)
}

export async function generateTOTP(secret, { digits = 6, period = 30, timestamp = Date.now() } = {}) {
  try {
    const keyBytes = base32Decode(secret)
    const counter = Math.floor(timestamp / 1000 / period)
    const counterBytes = new Uint8Array(8)
    let c = counter
    for (let i = 7; i >= 0; i--) {
      counterBytes[i] = c & 0xff
      c = Math.floor(c / 256)
    }
    const hmac = await hmacSha1(keyBytes, counterBytes)
    const offset = hmac[hmac.length - 1] & 0x0f
    const binary = ((hmac[offset] & 0x7f) << 24) |
      ((hmac[offset + 1] & 0xff) << 16) |
      ((hmac[offset + 2] & 0xff) << 8) |
      (hmac[offset + 3] & 0xff)
    return (binary % 10 ** digits).toString().padStart(digits, '0')
  } catch {
    return null
  }
}

export function parseOTPAuthURI(uri) {
  // otpauth://totp/Issuer:account@example.com?secret=BASE32&issuer=Issuer
  try {
    const url = new URL(uri)
    const params = url.searchParams
    const labelRaw = decodeURIComponent(url.pathname.replace(/^\//, '').replace(/^totp\//i, ''))
    // label format: "Issuer:account" — split on first ':' to extract account
    const colonIdx = labelRaw.indexOf(':')
    const issuerFromLabel = colonIdx > -1 ? labelRaw.slice(0, colonIdx) : ''
    const account = colonIdx > -1 ? labelRaw.slice(colonIdx + 1) : labelRaw
    const issuer = params.get('issuer') || issuerFromLabel || ''
    return {
      type: url.pathname.replace(/^\//, '').toLowerCase().split('/')[0], // 'totp' or 'hotp'
      label: account || issuer || 'Untitled',
      username: account,             // ← auto-fill username
      issuer,
      secret: params.get('secret'),
      algorithm: params.get('algorithm') || 'SHA1',
      digits: parseInt(params.get('digits') || '6', 10),
      period: parseInt(params.get('period') || '30', 10)
    }
  } catch {
    return null
  }
}

// Generate a random entry ID
export function generateId() {
  return crypto.randomUUID()
}

// Format time remaining for TOTP
export function getTOTPTimeRemaining(period = 30) {
  const now = Math.floor(Date.now() / 1000)
  return period - (now % period)
}

// Debounce utility
export function debounce(fn, ms) {
  let timer = null
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), ms)
  }
}

// Export/Import helpers
export async function exportVault(entries, password) {
  const data = {
    version: 1,
    exportedAt: new Date().toISOString(),
    entries: entries.map(e => ({
      id: e.id,
      label: e.label,
      vendor: e.vendor || '',
      ciphertext: e.ciphertext,
      iv: e.iv,
      salt: e.salt,
      createdAt: e.createdAt,
      updatedAt: e.updatedAt
    }))
  }
  return wrapData(data, password)
}

export async function importVault(file, password) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const wrapped = JSON.parse(e.target.result)
        const data = await unwrapData(wrapped, password)
        if (data.version !== 1 || !Array.isArray(data.entries)) {
          throw new Error('Invalid vault format')
        }
        resolve(data.entries)
      } catch (err) {
        reject(new Error('Failed to import: wrong password or corrupted file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}